import React, { useCallback } from "react"
import { IconEnvelopeCheckOutline18 as MailCheckIcon } from "nucleo-ui-outline-18"
import { z } from "zod"

import type { AllowedEmailDomain } from "@productlane/db/browser"
import {
	Dialog,
	Form,
	FormInput,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"

const FormSchema = z.object({
	code: z
		.string({ required_error: "You must provide a code" })
		.min(12, "You must provide a valid code"),
})

interface IProps {
	open: boolean
	data: Omit<AllowedEmailDomain, "hashedVerificationToken"> | null
	onClose: () => void
}

export const VerifyEmailDomainModal = React.memo(
	({ open, onClose, data }: IProps) => {
		const ctx = api.useUtils()
		const verifyAllowedDomainMutation =
			api.workspace.verifyAllowedEmailDomain.useMutation({
				onSuccess: async () => {
					await ctx.workspace.getAllowedEmailDomains.invalidate()
					toast.success({
						description:
							"Successfully created allowed domain! Please check the verification email inbox to activate the domain.",
						title: "Domain verification started",
					})
					onClose()
				},
			})

		const form = useZodForm({
			schema: FormSchema,
			defaultValues: {},
		})

		const handleSubmit = useCallback(
			async (values: z.infer<typeof FormSchema>) => {
				if (!data) return

				await verifyAllowedDomainMutation.mutateAsync({
					code: values.code,
					id: data.id,
				})
			},
			[verifyAllowedDomainMutation, data],
		)

		return (
			<Dialog
				onOpenChange={(value) => {
					if (!value) {
						onClose()
					}
				}}
				isOpen={open}
				title={
					<div className="flex items-center">
						<MailCheckIcon className="mr-2" />
						Verify domain {data?.domain}
					</div>
				}
				description={`A verification email was sent to ${data?.verificationEmail}. Please verify the domain before it can be used.`}
			>
				<Form form={form} className="relative" onSubmit={handleSubmit}>
					<div className="relative mt-3 flex flex-col space-y-4">
						<div className="space-y-3">
							<FormInput
								errorMessage={
									form.formState.errors.code?.message && form.formState.isDirty
								}
								name="code"
								label={
									<div>
										Verification code sent to{" "}
										<span className="font-mono font-semibold">
											{data?.verificationEmail}
										</span>
									</div>
								}
								placeholder="Enter the verification code"
								className="flex-1"
							/>
						</div>
						<div className="flex justify-end">
							{" "}
							<SubmitButton isLoading={verifyAllowedDomainMutation.isLoading}>
								Add domain
							</SubmitButton>
						</div>
					</div>
				</Form>
			</Dialog>
		)
	},
)
